import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Value,
} from 'react-phone-number-input';
import { User } from '/types/user-type';
import {
  ACCOUNT_QUERY_KEY,
  useGetUserById,
  useUpdateAccount,
} from 'features/profile/hooks/use-account';
import { UserDetailsData } from 'features/profile/services/accounts-service';
import { buildIamProfileUpdatePayload, haveRolesChanged } from 'features/profile/utils/utils';
import { useQueryClient } from '@tanstack/react-query';

import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from 'components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from 'components/ui/command';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from 'lib/utils';
import { IamData } from 'features/iam/services/user-service';
import UIPhoneInput from 'components/core/phone-input/phone-input';
import { Badge } from 'components/ui/badge';
import { useGetRolesQuery } from 'features/iam/hooks/use-iam';
import { useOrganizations } from 'features/organization/hooks/use-organization';
import { useTranslation } from 'react-i18next';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { ORGANIZATION_CONSTANTS } from 'constant/organization';
import { ROLES } from 'constant/roles';
import { useAuthStore } from 'state/store/auth/useAuthStore';
import { Label } from 'components/ui/label';

/**
 * `EditIamProfileDetails` component allows the user to edit their profile details, including their full name, email, phone number, country, and roles.
 * It integrates with the backend to fetch available roles, updates the account, and provides role selection with a limit of 5 roles.
 * The component supports form validation with mandatory country field and ensures the changes are saved to the server.
 *
 * Features:
 * - Full name editing
 * - Email display (read-only)
 * - Phone number with international format
 * - Searchable country selection with flags (mandatory)
 * - Role management with badge display
 * - Organization assignment for org-admin and org-user roles
 * - Form change detection
 * - Validation for required fields
 *
 * @component
 * @example
 * const userInfo = {
 *   fullName: 'John Doe',
 *   email: 'john.doe@example.com',
 *   phoneNumber: '+1234567890',
 *   country: 'United States',
 *   roles: ['admin', 'user'],
 *   itemId: '12345'
 * };
 *
 * <EditIamProfileDetails userInfo={userInfo} onClose={() => {}} />
 *
 * @param {Object} props - The component's props
 * @param {User | IamData} props.userInfo - The user information object containing current details to be edited
 * @param {Function} props.onClose - Callback function to close the dialog/modal
 *
 * @returns {React.Element} The rendered component
 */

type FormData = {
  itemId: string;
  fullName: string;
  email: string;
  phoneNumber: string;
  roles: string[];
  currentRole: string;
  organizationId: string;
};

type EditIamProfileDetailsProps = {
  userInfo: User | IamData;
  onClose: () => void;
};

const MAX_ROLES = 5;
const HIDDEN_ROLE_NAMES = new Set(['cloudadmin']);
const ALLOWED_EDIT_USER_ROLE_NAMES: Set<string> = new Set([ROLES.USER, ROLES.ADMIN]);

const resolveUserMembershipRoles = (
  userInfo: User | IamData,
  currentOrganizationId: string
) => {
  const memberships = 'memberships' in userInfo ? userInfo.memberships : undefined;

  if (!memberships?.length) {
    return [];
  }

  return (
    memberships.find((membership) => membership.organizationId === currentOrganizationId)?.roles ||
    memberships.find((membership) => membership.organizationId !== 'default')?.roles ||
    memberships[0]?.roles ||
    []
  );
};

export const EditIamProfileDetails: React.FC<EditIamProfileDetailsProps> = ({
  userInfo,
  onClose,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [availableRoles, setAvailableRoles] = useState<
    Array<{
      name: string;
      slug: string;
    }>
  >([]);
  const [organizationSearchTerm, setOrganizationSearchTerm] = useState('');
  const [organizationOpen, setOrganizationOpen] = useState(false);
  const isAdmin = useCommonStore((state) => state.isAdmin);
  const isOrgAdmin = useCommonStore((state) => state.isOrgAdmin);
  const currentUser = useCommonStore((state) => state.user);
  const selectedOrgId = useAuthStore((state) => state.selectedOrgId);
  const currentMembershipOrganizationId =
    selectedOrgId ||
    currentUser?.organizationId ||
    'default';

  // Fetch user details by ID
  const { data: fetchedUserData, isLoading: isLoadingUser } = useGetUserById(
    userInfo?.itemId ?? '',
    { enabled: !!userInfo?.itemId }
  );

  const { data: rolesData, isLoading: isLoadingRoles } = useGetRolesQuery({
    page: 0,
    pageSize: 100,
    filter: { search: '' },
    sort: { property: 'name', isDescending: false },
  });

  // Fetch organizations for autocomplete
  const { data: organizationsData } = useOrganizations({
    page: 0,
    pageSize: 50,
    name: organizationSearchTerm,
    enabled: true,
  });

  const organizations = (organizationsData?.data || []).filter(
    org => org.name !== ORGANIZATION_CONSTANTS.DEFAULT_ORGANIZATION_NAME
  );

  useEffect(() => {
    if (rolesData?.data) {
      const mappedRoles = rolesData.data
        .filter(role => {
          return ALLOWED_EDIT_USER_ROLE_NAMES.has(role.name) && !HIDDEN_ROLE_NAMES.has(role.name);
        })
        .map((role) => ({
          name: role.name,
          slug: role.name.toLowerCase().replace(/\s+/g, '-'),
        }));
      if (mappedRoles) setAvailableRoles(mappedRoles);
    }
  }, [isOrgAdmin, rolesData]);

  const { mutate: updateAccount, isPending } = useUpdateAccount({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ACCOUNT_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: ['getUsers'] });

      await queryClient.refetchQueries({
        queryKey: ACCOUNT_QUERY_KEY,
        type: 'active',
        exact: false,
      });

      onClose();
    },
  });

  const [isFormChanged, setIsFormChanged] = useState(false);

  const form = useForm<FormData>({
    defaultValues: {
      itemId: '',
      fullName: '',
      email: '',
      phoneNumber: '',
      roles: [],
      currentRole: '',
      organizationId: '',
    },
  });

  const { watch, setValue, handleSubmit, control } = form;
  const watchedValues = watch();
  const selectedOrganizationId = watch('organizationId');
  const selectedOrganization = organizations.find(org => org.itemId === selectedOrganizationId);
  const isOrgAdminOrUser = watchedValues.roles?.includes(ROLES.ADMIN) || watchedValues.roles?.includes(ROLES.USER);

  // Use fetched user data if available, otherwise fallback to passed userInfo
  const currentUserData = (fetchedUserData || userInfo) as UserDetailsData | User | IamData;

  useEffect(() => {
    if (currentUserData) {
      setValue('fullName', `${currentUserData.firstName ?? ''} ${currentUserData.lastName ?? ''}`.trim());
      setValue('email', currentUserData.email ?? '');
      setValue('phoneNumber', currentUserData.phoneNumber ?? '');
      setValue('itemId', currentUserData.itemId ?? '');

      // Handle organization assignment for edit mode
      let organizationId = '';
      if ('organizationId' in currentUserData) {
        // For cases where currentUserData has organizationId directly
        organizationId = (currentUserData as any).organizationId ?? '';
      } else if ('organizations' in currentUserData && Array.isArray((currentUserData as any).organizations)) {
        // For IamData type with organizations array, get the first organization
        const organizations = (currentUserData as any).organizations;
        if (organizations.length > 0) {
          const firstOrg = organizations[0];
          // Don't set organization if it's 'Default Organization'
          if (firstOrg.name !== ORGANIZATION_CONSTANTS.DEFAULT_ORGANIZATION_NAME) {
            organizationId = firstOrg.itemId;
          }
        }
      }
      setValue('organizationId', organizationId);

      // Map roles from fetched data
      const userRoles = currentUserData.roles || [];
      const tempRole = userRoles.filter((role: string) => ALLOWED_EDIT_USER_ROLE_NAMES.has(role));
      setValue('roles', tempRole.slice(0, MAX_ROLES));
    }
  }, [currentUserData, fetchedUserData, setValue]);

  useEffect(() => {
    if (!currentUserData) return;

    // Get initial organization ID
    let initialOrganizationId = '';
    if ('organizationId' in currentUserData) {
      initialOrganizationId = (currentUserData as any).organizationId ?? '';
    } else if ('organizations' in currentUserData && Array.isArray((currentUserData as any).organizations)) {
      const organizations = (currentUserData as any).organizations;
      if (organizations.length > 0) {
        const firstOrg = organizations[0];
        // Don't set organization if it's 'Default Organization'
        if (firstOrg.name !== ORGANIZATION_CONSTANTS.DEFAULT_ORGANIZATION_NAME) {
          initialOrganizationId = firstOrg.itemId;
        }
      }
    }

    const userRoles = currentUserData.roles || [];
    const initialValues = {
      fullName: `${currentUserData.firstName || ''} ${currentUserData.lastName || ''}`.trim(),
      phoneNumber: currentUserData.phoneNumber || '',
      profileImageUrl: currentUserData.profileImageUrl || '',
      roles: userRoles.filter((role: string) => ALLOWED_EDIT_USER_ROLE_NAMES.has(role)),
      organizationId: initialOrganizationId,
    };

    const rolesEqual =
      Array.isArray(watchedValues.roles) &&
      Array.isArray(initialValues.roles) &&
      watchedValues.roles.length === initialValues.roles.length &&
      watchedValues.roles.every((role) => initialValues.roles.includes(role));

    setIsFormChanged(
      watchedValues.fullName !== initialValues.fullName ||
      watchedValues.phoneNumber !== initialValues.phoneNumber ||
      watchedValues.organizationId !== initialValues.organizationId ||
      !rolesEqual
    );
  }, [currentUserData, fetchedUserData, watchedValues]);

  const onSubmit = async (data: FormData) => {
    const names = data.fullName.trim().split(' ');
    const firstName = names[0] || '';
    const lastName = names.slice(1).join(' ') || '';
    const scopedRoles = data.roles ?? [];
    const organizationIds = isAdmin
      ? [data.organizationId || currentMembershipOrganizationId]
      : organizationsData?.data?.map((org) => org.itemId) ?? [currentMembershipOrganizationId];
    const primaryOrgId = organizationIds[0] || currentMembershipOrganizationId;
    const previousRoles = (currentUserData.roles || []).filter((role: string) =>
      ALLOWED_EDIT_USER_ROLE_NAMES.has(role)
    );

    updateAccount({
      ...buildIamProfileUpdatePayload({
        itemId: data.itemId,
        firstName,
        lastName,
        email: data.email,
        phoneNumber: data.phoneNumber,
        country: ('country' in userInfo ? userInfo.country : '') ?? '',
        scopedRoles,
        organizationIds,
        primaryOrgId,
        existingOrgRoles:
          'OrganizationsRoles' in currentUserData
            ? currentUserData.OrganizationsRoles
            : undefined,
        existingOrgPermissions:
          'OrganizationsPermissions' in currentUserData
            ? currentUserData.OrganizationsPermissions
            : undefined,
      }),
      syncAccess: haveRolesChanged(scopedRoles, previousRoles),
      accessOrganizationId: primaryOrgId,
    });
  };

  const handleAddRole = (roleSlug: string) => {
    if (!roleSlug) return;

    const currentRoles = form.getValues('roles') || [];

    if (currentRoles.length >= MAX_ROLES) return;

    if (!currentRoles.includes(roleSlug)) {
      setValue('roles', [...currentRoles, roleSlug]);
    }

    if (roleSlug === "admin" && isAdmin) {
      setValue('organizationId', '');
    }

    setValue('currentRole', '');
  };

  const handleRemoveRole = (roleToRemove: string) => {
    const currentRoles = form.getValues('roles') || [];
    setValue(
      'roles',
      currentRoles.filter((role) => role !== roleToRemove)
    );

    if (roleToRemove == ROLES.ADMIN || roleToRemove == ROLES.USER) {
      setValue('organizationId', '');
    }
  };

  const handleDialogClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  const getAvailableRoles = () => {
    const selectedRoles = form.getValues('roles') || [];
    return availableRoles.filter((role) => !selectedRoles.includes(role.slug));
  };

  /*  const getRoleNameBySlug = (slug: string) => {
     const role = availableRoles.find((r) => r.slug === slug);
     return role ? role.name : slug;
   }; */

  const isMaxRolesReached = () => {
    const selectedRoles = form.getValues('roles') || [];
    return selectedRoles.length >= MAX_ROLES;
  };

  if (isLoadingUser) {
    return (
      <DialogContent
        className="sm:max-w-[500px] overflow-y-auto max-h-screen"
        onClick={handleDialogClick}
        hideClose
      >
        <div className="flex items-center justify-center p-8">
          <p className="text-muted-foreground">{t('LOADING')}...</p>
        </div>
      </DialogContent>
    );
  }

  return (
    <DialogContent
      className="sm:max-w-[500px] overflow-y-auto max-h-screen"
      onClick={handleDialogClick}
      hideClose
    >
      <DialogHeader className="pr-0">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <DialogTitle className="mb-2">{t('EDIT_PROFILE_DETAILS')}</DialogTitle>
            <DialogDescription className="text-medium-emphasis font-normal">
              {t('KEEP_DETAILS_ACCURATE_UP_TO_DATE')}
            </DialogDescription>
          </div>
          <button
            type="button"
            aria-label={t('CLOSE') || 'Close'}
            onClick={onClose}
            className="inline-flex h-4 w-6 shrink-0 items-center justify-center rounded-sm text-muted-foreground opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </DialogHeader>
      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
          <div className="grid grid-cols-1 gap-4">
            <FormField
              control={control}
              name="fullName"
              rules={{ required: t('FULL_NAME_REQUIRED') }}
              render={({ field }) => (
                <FormItem>
                  <Label>{t('FULL_NAME')}*</Label>
                  <FormControl>
                    <Input {...field} placeholder={t('ENTER_YOUR_FULL_NAME')} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <Label>{t('EMAIL')}</Label>
                  <FormControl>
                    <Input {...field} disabled />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="currentRole"
              rules={{ required: isAdmin || watchedValues.roles.length > 0 ? false : t('ROLES_REQUIRED') }}
              render={({ field }) => (
                <FormItem>
                  <Label>
                    {t('ROLES')} {isOrgAdmin ? "*" : ""}
                  </Label>
                  <div className="space-y-2">
                    <Select
                      onValueChange={(value) => {
                        if ((value == "admin" && isAdmin)) {
                          setValue('organizationId', '');
                        }
                        handleAddRole(value);
                      }}
                      value={field.value}
                      disabled={isMaxRolesReached() || isLoadingRoles}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              isLoadingRoles
                                ? `${t('LOADING_ROLES')}...`
                                : isMaxRolesReached()
                                  ? t('MAX_ROLES_REACHED')
                                  : t('SELECT_ROLES')
                            }
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {getAvailableRoles().map((role) => (
                          <SelectItem key={role.slug} value={role.slug}>
                            <span>{t(role.slug?.toUpperCase())}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <div className="flex flex-wrap gap-2 mt-2">
                      {watchedValues.roles?.map((roleSlug) => (
                        <Badge
                          key={roleSlug}
                          className="pr-1 flex items-center gap-1 text-[#fff] hover:bg-primary"
                        >
                          <span>{t(roleSlug?.toUpperCase())}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            className="h-4 w-4 p-0 hover:bg-transparent"
                            onClick={() => handleRemoveRole(roleSlug)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </Badge>
                      ))}
                    </div>
                    {/* {watchedValues.roles?.length > 0 && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {watchedValues.roles.length} of {MAX_ROLES} {t('ROLES_SELECTED')}
                      </p>
                    )} */}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="phoneNumber"
              // rules={{
              //   validate: (value) => {
              //     if (!value) return t('PHONE_NUMBER_REQUIRED');
              //     if (!isPossiblePhoneNumber(value)) return t('PHONE_NUMBER_LENGTH_INVALID');
              //     if (!isValidPhoneNumber(value)) return t('INVALID_PHONE_NUMBER');
              //     return true;
              //   },
              // }}
              render={({ field }) => (
                <FormItem>
                  <Label>{t('MOBILE_NO')}.</Label>
                  <FormControl>
                    <UIPhoneInput
                      {...field}
                      onChange={(value: Value) => setValue('phoneNumber', value ?? '')}
                      placeholder={t('ENTER_YOUR_MOBILE_NUMBER')}
                      defaultCountry="CH"
                      countryCallingCodeEditable={false}
                      international
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <DialogFooter className="mt-5 flex justify-end gap-2">
            <Button
              variant="outline"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
            >
              {t('CANCEL')}
            </Button>
            <Button
              type="submit"
              loading={isPending}
              disabled={isPending || !isFormChanged}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              {t('SAVE')}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </DialogContent>
  );
};
